import { Routes, Route } from 'react-router-dom';
import { UserRole } from '@/types';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { Layout } from '@/components/Layout';

// Pages
import { Dashboard } from '@/pages/Dashboard';
import { NotFound } from '@/pages/NotFound';

// Vendors
import { VendorList } from '@/pages/vendors/VendorList';
import { VendorDetail } from '@/pages/vendors/VendorDetail';
import { VendorForm } from '@/pages/vendors/VendorForm';
import { RiskAlerts } from '@/pages/vendors/RiskAlerts';

// Contracts
import { ContractList } from '@/pages/contracts/ContractList';
import { ContractDetail } from '@/pages/contracts/ContractDetail';
import { ContractForm } from '@/pages/contracts/ContractForm';
import { RenewalDecisionView } from '@/pages/contracts/RenewalDecisionView';

// Invoices
import { InvoiceList } from '@/pages/invoices/InvoiceList';
import { CodingQueue } from '@/pages/invoices/CodingQueue';
import { ApprovalQueue } from '@/pages/invoices/ApprovalQueue';
import { InvoiceDetail } from '@/pages/invoices/InvoiceDetail';

// Assets
import { AssetList } from '@/pages/assets/AssetList';
import { AssetDetail } from '@/pages/assets/AssetDetail';
import { RefreshForecast } from '@/pages/assets/RefreshForecast';
import { FleetAnalytics } from '@/pages/assets/FleetAnalytics';

// Circuits
import { CircuitList } from '@/pages/circuits/CircuitList';
import { CircuitDetail } from '@/pages/circuits/CircuitDetail';
import { BandwidthAnalytics } from '@/pages/circuits/BandwidthAnalytics';

// Sites
import { SitesHub } from '@/pages/sites/SitesHub';
import { SiteDetail } from '@/pages/sites/SiteDetail';
import { SiteForm } from '@/pages/sites/SiteForm';

// Reports
import { FinancialReport } from '@/pages/reports/FinancialReport';
import { ITSpendReport } from '@/pages/reports/ITSpendReport';
import { SpendAnalytics } from '@/pages/reports/SpendAnalytics';

// Budget
import { BudgetEditor } from '@/pages/budget/BudgetEditor';
import { BudgetVsActual } from '@/pages/budget/BudgetVsActual';

// Admin
import { UserRoles } from '@/pages/admin/UserRoles';
import { PlaybookManager } from '@/pages/admin/PlaybookManager';
import { DataImport } from '@/pages/admin/DataImport';
import { CredentialManager } from '@/pages/admin/CredentialManager';
import { HealthDashboard } from '@/pages/admin/HealthDashboard';
import { AppSettings } from '@/pages/admin/AppSettings';
import { AuditLogViewer } from '@/pages/admin/AuditLogViewer';
import { ErrorLog } from '@/pages/admin/ErrorLog';
import { LoginAuditLog } from '@/pages/admin/LoginAuditLog';
import { ScheduledReports } from '@/pages/admin/ScheduledReports';
import { APIRateMonitor } from '@/pages/admin/APIRateMonitor';
import { DataValidation } from '@/pages/admin/DataValidation';

// Settings
import { NotificationPreferences } from '@/pages/settings/NotificationPreferences';

// Search
import { SearchResults } from '@/pages/search/SearchResults';

// Employees
import { EmployeeList } from '@/pages/employees/EmployeeList';
import { EmployeeDetail } from '@/pages/employees/EmployeeDetail';

// AI Chat
import { AskNexus } from '@/pages/AskNexus';

// Service Desk Hub (combines Workload, Onboarding, Ticket Insights)
import { ServiceDeskHub } from '@/pages/ServiceDeskHub';

const { Admin, ITAdmin, ContractSpecialist, FacilitiesAdmin, Accounting, Controller, CFO, VPApprover, ServiceDesk } = UserRole;

export function AppRoutes() {
  return (
    <Routes>
      <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        {/* Dashboard */}
        <Route index element={<Dashboard />} />

        {/* Vendors */}
        <Route path="vendors" element={<VendorList />} />
        <Route path="vendors/new" element={
          <ProtectedRoute requiredRoles={[Admin, ContractSpecialist, FacilitiesAdmin]}>
            <VendorForm />
          </ProtectedRoute>
        } />
        <Route path="vendors/risk-alerts" element={
          <ProtectedRoute requiredRoles={[Admin, ServiceDesk]}>
            <RiskAlerts />
          </ProtectedRoute>
        } />
        <Route path="vendors/:id" element={<VendorDetail />} />

        {/* Contracts */}
        <Route path="contracts" element={<ContractList />} />
        <Route path="contracts/new" element={
          <ProtectedRoute requiredRoles={[Admin, ContractSpecialist, FacilitiesAdmin]}>
            <ContractForm />
          </ProtectedRoute>
        } />
        <Route path="contracts/:id" element={<ContractDetail />} />
        <Route path="contracts/:id/renewal" element={<RenewalDecisionView />} />

        {/* Invoices */}
        <Route path="invoices" element={<InvoiceList />} />
        <Route path="invoices/coding" element={
          <ProtectedRoute requiredRoles={[Admin, ContractSpecialist, FacilitiesAdmin]}>
            <CodingQueue />
          </ProtectedRoute>
        } />
        <Route path="invoices/approval" element={
          <ProtectedRoute requiredRoles={[Admin, VPApprover]}>
            <ApprovalQueue />
          </ProtectedRoute>
        } />
        <Route path="invoices/:id" element={<InvoiceDetail />} />

        {/* Assets */}
        <Route path="assets" element={
          <ProtectedRoute requiredRoles={[Admin, ITAdmin, ContractSpecialist, Controller, CFO, ServiceDesk]}>
            <AssetList />
          </ProtectedRoute>
        } />
        <Route path="assets/refresh-forecast" element={
          <ProtectedRoute requiredRoles={[Admin, ITAdmin]}>
            <RefreshForecast />
          </ProtectedRoute>
        } />
        <Route path="assets/fleet-analytics" element={
          <ProtectedRoute requiredRoles={[Admin, ITAdmin, ServiceDesk]}>
            <FleetAnalytics />
          </ProtectedRoute>
        } />
        <Route path="assets/:id" element={
          <ProtectedRoute requiredRoles={[Admin, ITAdmin, ContractSpecialist, Controller, CFO, ServiceDesk]}>
            <AssetDetail />
          </ProtectedRoute>
        } />

        {/* Circuits */}
        <Route path="circuits" element={
          <ProtectedRoute requiredRoles={[Admin, ITAdmin, ContractSpecialist, ServiceDesk]}>
            <CircuitList />
          </ProtectedRoute>
        } />
        <Route path="circuits/bandwidth" element={
          <ProtectedRoute requiredRoles={[Admin, ITAdmin, ContractSpecialist, Controller, CFO]}>
            <BandwidthAnalytics />
          </ProtectedRoute>
        } />
        <Route path="circuits/:id" element={
          <ProtectedRoute requiredRoles={[Admin, ITAdmin, ContractSpecialist, ServiceDesk]}>
            <CircuitDetail />
          </ProtectedRoute>
        } />

        {/* Sites */}
        <Route path="sites" element={<SitesHub />} />
        <Route path="sites/new" element={
          <ProtectedRoute requiredRoles={[Admin, ContractSpecialist]}>
            <SiteForm />
          </ProtectedRoute>
        } />
        <Route path="sites/:id/edit" element={
          <ProtectedRoute requiredRoles={[Admin, ContractSpecialist]}>
            <SiteForm />
          </ProtectedRoute>
        } />
        <Route path="sites/:id" element={<SiteDetail />} />

        {/* Employees */}
        <Route path="employees" element={<EmployeeList />} />
        <Route path="employees/:id" element={<EmployeeDetail />} />

        {/* Reports */}
        <Route path="reports/financial" element={
          <ProtectedRoute requiredRoles={[Admin, Accounting, Controller, CFO]}>
            <FinancialReport />
          </ProtectedRoute>
        } />
        <Route path="reports/it-spend" element={
          <ProtectedRoute requiredRoles={[Admin, Controller, CFO]}>
            <ITSpendReport />
          </ProtectedRoute>
        } />
        <Route path="reports/spend-analytics" element={
          <ProtectedRoute requiredRoles={[Admin, Controller, CFO]}>
            <SpendAnalytics />
          </ProtectedRoute>
        } />

        {/* Budget */}
        <Route path="budget" element={
          <ProtectedRoute requiredRoles={[Admin]}>
            <BudgetEditor />
          </ProtectedRoute>
        } />
        <Route path="budget/vs-actual" element={
          <ProtectedRoute requiredRoles={[Admin, Controller, CFO]}>
            <BudgetVsActual />
          </ProtectedRoute>
        } />

        {/* Admin */}
        <Route path="admin/users" element={
          <ProtectedRoute requiredRoles={[Admin]}>
            <UserRoles />
          </ProtectedRoute>
        } />
        <Route path="admin/playbook" element={
          <ProtectedRoute requiredRoles={[Admin, ITAdmin, ContractSpecialist]}>
            <PlaybookManager />
          </ProtectedRoute>
        } />
        <Route path="admin/import" element={
          <ProtectedRoute requiredRoles={[Admin, ITAdmin, Controller, CFO]}>
            <DataImport />
          </ProtectedRoute>
        } />
        <Route path="admin/credentials" element={
          <ProtectedRoute requiredRoles={[Admin, ITAdmin]}>
            <CredentialManager />
          </ProtectedRoute>
        } />
        <Route path="admin/health" element={
          <ProtectedRoute requiredRoles={[Admin, ITAdmin]}>
            <HealthDashboard />
          </ProtectedRoute>
        } />
        <Route path="admin/settings" element={
          <ProtectedRoute requiredRoles={[Admin, ITAdmin]}>
            <AppSettings />
          </ProtectedRoute>
        } />
        <Route path="admin/audit" element={
          <ProtectedRoute requiredRoles={[Admin, ITAdmin, ContractSpecialist]}>
            <AuditLogViewer />
          </ProtectedRoute>
        } />
        <Route path="admin/errors" element={
          <ProtectedRoute requiredRoles={[Admin, ITAdmin]}>
            <ErrorLog />
          </ProtectedRoute>
        } />
        <Route path="admin/login-audit" element={
          <ProtectedRoute requiredRoles={[Admin, ITAdmin]}>
            <LoginAuditLog />
          </ProtectedRoute>
        } />
        <Route path="admin/scheduled-reports" element={<ScheduledReports />} />
        <Route path="admin/api-rates" element={
          <ProtectedRoute requiredRoles={[Admin, ITAdmin]}>
            <APIRateMonitor />
          </ProtectedRoute>
        } />
        <Route path="admin/data-validation" element={
          <ProtectedRoute requiredRoles={[Admin, ITAdmin, ServiceDesk]}>
            <DataValidation />
          </ProtectedRoute>
        } />

        {/* Settings */}
        <Route path="settings/notifications" element={<NotificationPreferences />} />

        {/* Search */}
        <Route path="search" element={<SearchResults />} />

        {/* AI Chat */}
        <Route path="ask" element={<AskNexus />} />

        {/* Service Desk Hub (Workload + Onboarding + Ticket Insights) */}
        <Route path="service-desk" element={
          <ProtectedRoute requiredRoles={[Admin, ITAdmin]}>
            <ServiceDeskHub />
          </ProtectedRoute>
        } />

        {/* 404 */}
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}
